import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { translateAnalysis } from "@/lib/edgeFunctions";
// ==========================================
// Type Definitions for Analysis Data
// ==========================================

// N8N Pick format from workflow
export interface N8NPick {
  id: string;
  label: string;
  market: string;
  odd?: number;
  tier?: 'safe' | 'risky';
  source?: string;
  no_bet?: boolean;
  rationale?: string;
  selection?: string;
}

export interface AnalysisReport {
  md_article?: string;
  html_article?: string;
  summary?: string;
  highlights?: string[];
  tags?: string[];
  sidebars?: {
    key_facts?: Record<string, { rank: number; points: number; form: string }>;
    disciplina?: Record<string, { yellow_cards: number; red_cards: number }>;
    minutos_chave?: Record<string, Record<string, string>>;
  };
  confidence?: number;
  disclaimers?: string;
  source_notes?: string;
  picks?: N8NPick[];
  sugestoes_estatisticas_extra?: string[];
}

export interface QaDiag {
  present?: {
    fixture?: boolean;
    standings?: boolean;
    odds?: boolean;
    h2h?: boolean;
    injuries?: boolean;
    recent_form?: boolean;
    resume?: boolean;
    top_scorers?: boolean;
    team_stats_home?: boolean;
    team_stats_away?: boolean;
    opponent_strength?: boolean;
    predictions?: boolean;
  };
}

// SG7 Context - structured payload from N8N workflow (optional, v1.0+)
export interface SG7Context {
  version?: string;
  fixture_id?: number;
  ids?: {
    home_id?: number;
    away_id?: number;
    league_id?: number;
  };
  standings?: Record<string, unknown>;
  team_stats?: Record<string, unknown>;  // includes timing_buckets (goals_by_period)
  recent_form?: Record<string, unknown>;
  h2h?: unknown[];
  opponent_strength?: {
    home_osa?: number;
    away_osa?: number;
    assessment?: string;
  };
  value_gate?: {
    passed?: boolean;
    reasons?: string[];
  };
  odds?: {
    catalog_timing?: Record<string, unknown>;
    shortlist?: unknown[];
  };
  picks?: {
    pick_candidates_ui?: unknown[];
    pick_candidates_all?: unknown[];
  };
}

export interface SafeNumbers {
  home?: { rank: number; points: number };
  away?: { rank: number; points: number };
  odds_ft_1x2?: {
    home: number;
    draw: number;
    away: number;
    favorite?: { selection: string; prob: number };
    source?: string;
    updatedAt?: string;
  };
}

export interface AnalysisData {
  fixture_id: number;
  sg7_context: SG7Context | null;
  status: 'pending' | 'processing' | 'ready' | 'error';
  ai_output: AnalysisReport | null;
  payload_snapshot: Record<string, unknown> | null;
  qa_diag: QaDiag | null;
  safe_numbers: SafeNumbers | null;
  confidence_score: number | null;
  error_message: string | null;
  updated_at: string;
  // Legacy fields for backwards compatibility
  summary?: string | null;
  content_html?: string | null;
  content_md?: string | null;
}

interface UseAnalysisOptions {
  pollInterval?: number; // ms, default 5000
  lang?: string; // ISO 639-1 language code, default 'pt' (canonical)
}

// ==========================================
// Main Hook: useAnalysis (Read-Only + Translation)
// ==========================================

export function useAnalysis(fixtureId: number | null, options: UseAnalysisOptions = {}) {
  const [analysis, setAnalysis] = useState<AnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const { pollInterval = 5000, lang = 'pt' } = options;

  // Fetch existing analysis from DB (read-only)
  const fetchAnalysis = useCallback(async () => {
    if (!fixtureId) {
      setAnalysis(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('analyses')
        .select('*')
        .eq('fixture_id', fixtureId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching analysis:', error);
        setAnalysis(null);
        setLoading(false);
        return;
      }

      if (data) {
        // Determine status from DB or infer from ready flag
        const status = (data.status as AnalysisData['status']) || 
                       (data.ready ? 'ready' : 'pending');

        setAnalysis({
          fixture_id: data.fixture_id,
          status,
          ai_output: data.ai_output as AnalysisReport | null,
          payload_snapshot: data.payload_snapshot as Record<string, unknown> | null,
          qa_diag: data.qa_diag as QaDiag | null,
          safe_numbers: data.safe_numbers as SafeNumbers | null,
          sg7_context: (data as { sg7_context?: unknown }).sg7_context as SG7Context | null,
          confidence_score: data.confidence_score,
          error_message: data.error_message,
          updated_at: data.updated_at || new Date().toISOString(),
          // Legacy fields
          summary: data.summary,
          content_html: data.content_html,
          content_md: data.content_md
        });
      } else {
        // No analysis row exists yet - show as pending
        // The precompute-analyses cron will create it automatically
        setAnalysis({
          fixture_id: fixtureId,
          status: 'pending',
          ai_output: null,
          payload_snapshot: null,
          qa_diag: null,
          safe_numbers: null,
          sg7_context: null,
          confidence_score: null,
          error_message: null,
          updated_at: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error in fetchAnalysis:', error);
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  }, [fixtureId]);

  // Initial fetch
  useEffect(() => {
    setLoading(true);
    fetchAnalysis();
  }, [fetchAnalysis]);

  // Polling while status === 'processing'
  useEffect(() => {
    if (analysis?.status === 'processing') {
      const interval = setInterval(fetchAnalysis, pollInterval);
      return () => clearInterval(interval);
    }
  }, [analysis?.status, fetchAnalysis, pollInterval]);

  // Translation layer: when lang !== 'pt' and analysis is ready, fetch translation
  useEffect(() => {
    if (!analysis || analysis.status !== 'ready' || !lang || lang === 'pt' || !fixtureId) {
      setTranslating(false);
      return;
    }

    let cancelled = false;
    setTranslating(true);

    translateAnalysis(fixtureId, lang)
      .then((translated) => {
        if (cancelled) return;
        // Merge translated text fields into the analysis object
        setAnalysis(prev => {
          if (!prev) return prev;
          const aiOutput = prev.ai_output ? { ...prev.ai_output } : null;
          if (aiOutput && translated.md_article) {
            aiOutput.md_article = translated.md_article;
          }
          if (aiOutput && translated.highlights) {
            aiOutput.highlights = translated.highlights;
          }
          if (aiOutput && translated.summary) {
            aiOutput.summary = translated.summary;
          }
          // Merge translated tags
          if (aiOutput && translated.tags) {
            aiOutput.tags = translated.tags;
          }
          // Merge translated pick rationales into picks array
          if (aiOutput && aiOutput.picks && translated.pick_rationales) {
            const rationaleMap = new Map(
              translated.pick_rationales.map((p: { id: string; rationale: string }) => [p.id, p.rationale])
            );
            aiOutput.picks = aiOutput.picks.map((pick: N8NPick) => ({
              ...pick,
              rationale: (rationaleMap.get(pick.id) || pick.rationale) as string | undefined,
            }));
          }
          return {
            ...prev,
            ai_output: aiOutput,
            summary: translated.summary || prev.summary,
            // Replace content_md with translated article for legacy consumers
            content_md: translated.md_article || prev.content_md,
          };
        });
      })
      .catch((err) => {
        console.warn('[useAnalysis] Translation failed, showing PT original:', err.message);
      })
      .finally(() => {
        if (!cancelled) setTranslating(false);
      });

    return () => { cancelled = true; };
  }, [analysis?.status, lang, fixtureId]);

  return { 
    analysis, 
    loading, 
    translating,
    refetch: fetchAnalysis
    // NOTE: No triggerAnalysis exposed - frontend is read-only
    // Analyses are precomputed by the Supabase cron job
  };
}
